import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Package, Truck } from "lucide-react";

export default function PinShipmentsCard() {
  const [shipments, setShipments] = useState([]);
  const [shippingId, setShippingId] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});

  const load = useCallback(
    () => api.get("/admin/pin-shipments").then((r) => setShipments(r.data || [])),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const markShipped = async (s) => {
    setShippingId(s.id);
    try {
      const tracking = trackingInputs[s.id] || "";
      await api.post(`/admin/pin-shipments/${s.id}/ship`, {
        tracking_number: tracking || null,
      });
      toast(`Shipped #${String(s.member_number).padStart(3, "0")}`);
      await load();
    } catch {
      toast("Failed to mark shipped.");
    } finally {
      setShippingId(null);
    }
  };

  return (
    <div className="tac-card p-7 mt-8" data-testid="pin-shipments-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2">
            <Package size={12} /> Founding pin shipments
          </div>
          <h2 className="font-display text-3xl uppercase mt-2">Pending fulfillments</h2>
        </div>
        <div className="font-mono-tactical text-xs text-white/50 uppercase tracking-[0.25em]">
          {shipments.filter((s) => s.status === "pending").length} pending ·{" "}
          {shipments.filter((s) => s.status === "shipped").length} shipped
        </div>
      </div>

      <div className="mt-6 divide-y divide-white/5">
        {shipments.length === 0 && (
          <div className="py-8 text-white/60 text-sm">
            No pin claims yet. They'll show here the moment a Founding Member submits their address.
          </div>
        )}
        {shipments.map((s) => (
          <div key={s.id} className="py-4 grid md:grid-cols-12 gap-4 items-center" data-testid={`shipment-${s.id}`}>
            <div className="md:col-span-2">
              <div className="font-display text-3xl text-[#FFC107]">
                #{String(s.member_number || 0).padStart(3, "0")}
              </div>
              <div className="font-mono-tactical text-[10px] text-white/50 uppercase tracking-[0.2em]">
                {new Date(s.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="md:col-span-5 text-sm">
              <div className="font-semibold">{s.full_name}</div>
              <div className="text-white/60 mt-1">
                {s.address_line1}
                {s.address_line2 ? `, ${s.address_line2}` : ""}, {s.city}, {s.state}{" "}
                {s.postal_code}, {s.country}
              </div>
              <div className="text-white/40 text-xs mt-1 font-mono-tactical">{s.user_email}</div>
            </div>
            <div className="md:col-span-5 flex items-center gap-2 justify-end flex-wrap">
              {s.status === "shipped" ? (
                <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#22C55E]/40 px-3 py-2 text-xs">
                  <Truck size={14} className="text-[#22C55E]" />
                  <span className="font-display tracking-[0.18em]">Shipped</span>
                  {s.tracking_number && (
                    <span className="font-mono-tactical text-white/60 ml-1">
                      · {s.tracking_number}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <input
                    className="tac-input flex-1 min-w-[140px] text-xs"
                    placeholder="Tracking # (optional)"
                    value={trackingInputs[s.id] || ""}
                    onChange={(e) =>
                      setTrackingInputs({ ...trackingInputs, [s.id]: e.target.value })
                    }
                    data-testid={`shipment-tracking-${s.id}`}
                  />
                  <button
                    onClick={() => markShipped(s)}
                    disabled={shippingId === s.id}
                    className="btn-blaze text-xs px-4 py-2"
                    data-testid={`shipment-ship-${s.id}`}
                  >
                    <Truck size={12} />
                    {shippingId === s.id ? "Shipping..." : "Mark shipped"}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
